/**
 * useAudioRecording Hook
 *
 * Manages audio recording using the browser's MediaRecorder API.
 * Provides chunked recording for real-time transcription.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import type { RecordingState, RecordingMetadata } from '@ispot/shared'

export interface UseAudioRecordingOptions {
  /** Callback when an audio chunk is ready */
  onChunkReady?: (chunk: Blob, chunkIndex: number) => void
  /** Interval for chunking audio (ms). Default: 5000 (5 seconds) */
  chunkIntervalMs?: number
  /** Preferred MIME type. Default: audio/webm;codecs=opus */
  mimeType?: string
}

export interface UseAudioRecordingReturn {
  /** Current recording state */
  state: RecordingState
  /** Recording duration in seconds */
  duration: number
  /** Recording metadata */
  metadata: RecordingMetadata | null
  /** Error if any */
  error: Error | null
  /** Start recording */
  start: () => Promise<void>
  /** Stop recording and return final audio blob */
  stop: () => Promise<Blob | null>
  /** Pause recording */
  pause: () => void
  /** Resume recording */
  resume: () => void
  /** Check if browser supports audio recording */
  isSupported: boolean
}

// Default chunk interval (5 seconds)
const DEFAULT_CHUNK_INTERVAL = 5000

// Preferred MIME types in order of preference
const MIME_TYPE_PREFERENCES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
]

/**
 * Get the best supported MIME type for recording
 */
function getSupportedMimeType(preferred?: string): string {
  if (preferred && MediaRecorder.isTypeSupported(preferred)) {
    return preferred
  }

  for (const type of MIME_TYPE_PREFERENCES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }

  return '' // Let browser choose default
}

export function useAudioRecording(
  options: UseAudioRecordingOptions = {}
): UseAudioRecordingReturn {
  const {
    onChunkReady,
    chunkIntervalMs = DEFAULT_CHUNK_INTERVAL,
    mimeType: preferredMimeType,
  } = options

  // State
  const [state, setState] = useState<RecordingState>('idle')
  const [duration, setDuration] = useState(0)
  const [metadata, setMetadata] = useState<RecordingMetadata | null>(null)
  const [error, setError] = useState<Error | null>(null)

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const chunkIndexRef = useRef(0)
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<Date | null>(null)

  // Check browser support
  const isSupported = typeof navigator !== 'undefined' &&
    'mediaDevices' in navigator &&
    'getUserMedia' in navigator.mediaDevices &&
    typeof MediaRecorder !== 'undefined'

  /**
   * Start duration tracking
   */
  const startDurationTracking = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
    }

    durationIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = (Date.now() - startTimeRef.current.getTime()) / 1000
        setDuration(Math.floor(elapsed))
      }
    }, 100)
  }, [])

  /**
   * Stop duration tracking
   */
  const stopDurationTracking = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
  }, [])

  /**
   * Start recording
   */
  const start = useCallback(async () => {
    if (!isSupported) {
      setError(new Error('Audio recording is not supported in this browser'))
      setState('error')
      return
    }

    try {
      setError(null)
      setState('processing')

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      streamRef.current = stream

      // Get best supported MIME type
      const mimeType = getSupportedMimeType(preferredMimeType)

      // Create MediaRecorder with chunking
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
      })

      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []
      chunkIndexRef.current = 0

      // Handle data available (chunks)
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)

          // Call chunk callback for real-time transcription
          // IMPORTANT: Send ALL accumulated chunks as a complete WebM file
          // Individual WebM chunks lack the initialization header and are invalid
          if (onChunkReady) {
            const completeBlob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType })
            onChunkReady(completeBlob, chunkIndexRef.current)
            chunkIndexRef.current++
          }
        }
      }

      // Handle recording stop
      mediaRecorder.onstop = () => {
        stopDurationTracking()
      }

      // Handle errors
      mediaRecorder.onerror = (event) => {
        const errorEvent = event as ErrorEvent
        setError(new Error(errorEvent.message || 'Recording error'))
        setState('error')
        stopDurationTracking()
      }

      // Start recording with timeslice for chunking
      startTimeRef.current = new Date()
      mediaRecorder.start(chunkIntervalMs)

      // Update metadata
      setMetadata({
        duration: 0,
        mimeType: mediaRecorder.mimeType,
        chunkCount: 0,
        startedAt: startTimeRef.current.toISOString(),
      })

      // Start tracking duration
      startDurationTracking()
      setState('recording')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start recording'

      // Handle specific permission errors
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError(new Error('Microphone permission denied. Please allow access to record.'))
        } else if (err.name === 'NotFoundError') {
          setError(new Error('No microphone found. Please connect a microphone.'))
        } else {
          setError(new Error(message))
        }
      } else {
        setError(new Error(message))
      }

      setState('error')
    }
  }, [isSupported, preferredMimeType, chunkIntervalMs, onChunkReady, startDurationTracking, stopDurationTracking])

  /**
   * Stop recording
   */
  const stop = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current

      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(null)
        return
      }

      setState('processing')

      // Wait for final data
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        // Combine all chunks into final blob
        const mimeType = mediaRecorder.mimeType || 'audio/webm'
        const finalBlob = new Blob(chunksRef.current, { type: mimeType })

        // Update metadata
        setMetadata((prev) => prev ? {
          ...prev,
          duration,
          chunkCount: chunksRef.current.length,
          endedAt: new Date().toISOString(),
        } : null)

        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }

        stopDurationTracking()
        setState('complete')
        resolve(finalBlob)
      }

      mediaRecorder.stop()
    })
  }, [duration, stopDurationTracking])

  /**
   * Pause recording
   */
  const pause = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current

    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause()
      stopDurationTracking()
      setState('paused')
    }
  }, [stopDurationTracking])

  /**
   * Resume recording
   */
  const resume = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current

    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume()
      startDurationTracking()
      setState('recording')
    }
  }, [startDurationTracking])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDurationTracking()

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [stopDurationTracking])

  return {
    state,
    duration,
    metadata,
    error,
    start,
    stop,
    pause,
    resume,
    isSupported,
  }
}
